import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery
} from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { queryClient } from '@/adapters/query';
import { usersv2Client } from '@/adapters/schemaClients';
import { UserModel } from '@/models/UserModel';

import { ALL_FACILITIES } from './useFacilitiesQuery';

interface FetchAllUsersV2Params {
  facilityId?: null | string | string[] | typeof ALL_FACILITIES;
  statuses?: null | string | string[];
  requireCrmPermissions?: boolean;
  limit?: number;
  page?: number;
  name?: string;
  userId?: string[];
}

interface FetchAllUsersV2Response {
  page: number;
  limit: number;
  has_next: boolean;
  total: number;
  users: UserModel[];
}

export const FIND_ALL_USERS_V2_QUERY_KEY = 'findAllUsersV2';
export const FIND_ALL_USERS_V2_INFINITE_QUERY_KEY = 'findAllUsersV2Infinite';
export const FIND_ALL_USERS_V2_DEFAULT_LIMIT = 50;

export const invalidateUsersFindAllV2Query = () => {
  queryClient.invalidateQueries([FIND_ALL_USERS_V2_QUERY_KEY]);
  queryClient.invalidateQueries([FIND_ALL_USERS_V2_INFINITE_QUERY_KEY]);
};

const useFindAllUsersV2Query = (
  params: FetchAllUsersV2Params,
  options: UseQueryOptions<FetchAllUsersV2Response, HTTPError> = {}
) =>
  useQuery<FetchAllUsersV2Response, HTTPError>(
    [FIND_ALL_USERS_V2_QUERY_KEY, params],
    async () => fetchAllUsersV2(params),
    {
      ...options
    }
  );

const findAllUsersV2InfiniteQuery = (
  params: FetchAllUsersV2Params,
  options: UseInfiniteQueryOptions<FetchAllUsersV2Response, HTTPError> = {}
) =>
  useInfiniteQuery<FetchAllUsersV2Response>(
    [FIND_ALL_USERS_V2_INFINITE_QUERY_KEY, params],
    async ({ pageParam }) => {
      const page = pageParam ? Number(pageParam.page) + 1 : 1;
      return await fetchAllUsersV2({ ...params, page });
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage?.has_next ? lastPage : undefined,
      ...(options as any)
    }
  );

export const useUsersFindAllV2Query = () => {
  return {
    invalidateUsersFindAllV2Query,
    useFindAll: useFindAllUsersV2Query,
    findAllUsersV2InfiniteQuery: findAllUsersV2InfiniteQuery
  };
};

async function fetchAllUsersV2({
  facilityId,
  limit,
  page,
  name,
  statuses,
  userId
}: FetchAllUsersV2Params = {}): Promise<FetchAllUsersV2Response> {
  const getFacilityParams = () => {
    if (!facilityId || facilityId === ALL_FACILITIES) {
      return '';
    }

    const facilityIds = Array.isArray(facilityId) ? facilityId : [facilityId];
    return facilityIds.join(',');
  };

  const getStatusParams = () => {
    if (!statuses) {
      return undefined;
    }

    const statusList = Array.isArray(statuses) ? statuses : [statuses];
    return statusList.join(',');
  };

  const getUserIdParams = () => {
    if (!userId) {
      return undefined;
    }

    const userIds = Array.isArray(userId) ? userId : [userId];
    return userIds.join(',');
  };

  const response = await usersv2Client.findAllUsersV2({
    page,
    limit,
    facilityIds: getFacilityParams(),
    statuses: getStatusParams(),
    name,
    userId: getUserIdParams()
  });

  return {
    page: response.data?.page || 0,
    limit: response.data?.limit || 0,
    has_next: response.data?.has_next || false,
    total: response.data?.total || 0,
    users: response.data?.data.map((user) => new UserModel(user as any)) || []
  };
}
